import { Bell, BellOff, Trash2, ChevronDown, ChevronUp, RefreshCw, Play, ExternalLink, UserPlus, Clock, ImageOff } from 'lucide-react';
import { Button } from '../ui/Button';
import type { SavedSearch, Listing, AlertResult } from '../../services/api';
import { getContractTypeLabel, formatPrice, formatSurface, getPlatformLabel } from '../../lib/utils';

interface SearchCardProps {
  search: SavedSearch;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleNotifications: (search: SavedSearch) => void;
  onDelete: (search: SavedSearch) => void;
  onRunSearch: (search: SavedSearch) => void;
  onSaveAsLead: (listing: Listing, searchId: string) => void;
  savedExternalIds: Set<string>;
  isDeleting: boolean;
}

function formatFilters(filters: SavedSearch['filters']): string {
  const parts: string[] = [];
  if (filters.city) parts.push(filters.city);
  if (filters.contractType) parts.push(getContractTypeLabel(filters.contractType as 'SALE' | 'RENT'));
  if (filters.minPrice || filters.maxPrice) {
    const min = filters.minPrice ? `${(filters.minPrice / 1000).toFixed(0)}k` : '0';
    const max = filters.maxPrice ? `${(filters.maxPrice / 1000).toFixed(0)}k` : '+';
    parts.push(`${min} - ${max} EUR`);
  }
  if (filters.minRooms || filters.maxRooms) {
    const min = filters.minRooms || '1';
    const max = filters.maxRooms || '+';
    parts.push(`${min}-${max} locali`);
  }
  return parts.length > 0 ? parts.join(' | ') : 'Tutti gli annunci';
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}g fa`;
  const months = Math.floor(days / 30);
  return `${months} mesi fa`;
}

// Convert AlertResult to a minimal Listing for SaveAsLeadDialog
function alertResultToListing(result: AlertResult): Listing {
  return {
    id: result.id,
    externalId: result.externalId,
    platform: result.platform,
    title: result.title,
    price: result.price,
    contractType: result.contractType as 'SALE' | 'RENT',
    city: result.city,
    surface: result.surface,
    rooms: result.rooms,
    bathrooms: result.bathrooms,
    sourceUrl: result.sourceUrl,
    images: result.imageUrl ? [{ url: result.imageUrl, label: null }] : [],
    createdAt: result.foundAt,
  } as Listing;
}

export function SearchCard({
  search,
  isExpanded,
  onToggleExpand,
  onToggleNotifications,
  onDelete,
  onRunSearch,
  onSaveAsLead,
  savedExternalIds,
  isDeleting,
}: SearchCardProps) {
  const alertResults = search.lastAlertResults || [];
  const newCount = alertResults.length;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden transition-all hover:shadow-md">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggleExpand}>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">{search.name}</h3>
              {search.notifyEnabled ? (
                <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <Bell className="h-2.5 w-2.5" />
                  Attivo
                </span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                  <BellOff className="h-2.5 w-2.5" />
                  Off
                </span>
              )}
              {newCount > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-white bg-orange-500 px-2 py-0.5 rounded-full flex-shrink-0 font-semibold">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                  </span>
                  {newCount} nuov{newCount === 1 ? 'o' : 'i'}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">{formatFilters(search.filters)}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              variant="default"
              size="sm"
              onClick={() => onRunSearch(search)}
              className="h-8 text-xs px-2"
            >
              <Play className="h-3 w-3 mr-1" />
              Cerca
            </Button>
            <button
              onClick={() => onToggleNotifications(search)}
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              title={search.notifyEnabled ? 'Disattiva notifiche' : 'Attiva notifiche'}
            >
              {search.notifyEnabled ? (
                <BellOff className="h-3.5 w-3.5 text-gray-600" />
              ) : (
                <Bell className="h-3.5 w-3.5 text-gray-600" />
              )}
            </button>
            <button
              onClick={() => onDelete(search)}
              disabled={isDeleting}
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
              title="Elimina ricerca"
            >
              {isDeleting ? (
                <RefreshCw className="h-3.5 w-3.5 text-red-500 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              )}
            </button>
            <button
              onClick={onToggleExpand}
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              title={isExpanded ? 'Chiudi' : 'Mostra nuovi annunci'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded: alert results recap */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50/50">
          {alertResults.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">
              Nessun nuovo annuncio trovato ancora.
              <br />
              <span className="text-xs">Il worker controlla ogni 5 minuti.</span>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 mb-2">
                {alertResults.length} annunc{alertResults.length === 1 ? 'io' : 'i'} trovat{alertResults.length === 1 ? 'o' : 'i'} dall'alert
              </p>
              {alertResults.map((result) => {
                const isSaved = savedExternalIds.has(result.externalId);
                return (
                  <div key={result.id} className="flex gap-3 p-3 bg-white rounded-xl border border-gray-100">
                    {/* Listing thumbnail */}
                    <a
                      href={result.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100"
                    >
                      {result.imageUrl ? (
                        <img
                          src={result.imageUrl}
                          alt={result.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageOff className="h-5 w-5" />
                        </div>
                      )}
                    </a>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <a href={result.sourceUrl} target="_blank" rel="noopener noreferrer" className="block">
                        <h4 className="text-sm font-medium text-gray-900 leading-tight hover:text-blue-600 transition-colors line-clamp-2">{result.title}</h4>
                      </a>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                        {result.price != null && (
                          <span className="font-semibold text-gray-900">
                            {formatPrice(result.price, result.contractType as 'SALE' | 'RENT')}
                          </span>
                        )}
                        {result.surface != null && <span>{formatSurface(result.surface)}</span>}
                        {result.rooms != null && <span>{result.rooms} loc</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {getPlatformLabel(result.platform)}
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          <Clock className="h-2.5 w-2.5" />
                          {formatTimeAgo(result.createdAt || result.foundAt)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          size="sm"
                          variant={isSaved ? 'outline' : 'default'}
                          className="h-7 text-xs px-2"
                          onClick={() => onSaveAsLead(alertResultToListing(result), search.id)}
                          disabled={isSaved}
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          {isSaved ? 'Salvato' : 'Salva Lead'}
                        </Button>
                        {result.sourceUrl && (
                          <a
                            href={result.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-7 w-7 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                            title="Apri originale"
                          >
                            <ExternalLink className="h-3 w-3 text-gray-600" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
